/**
 * @fileoverview UsageBanner — soft warning + hard-block banner
 *
 * Surfaces the current org's AI usage state above the chat input:
 * - At ≥ $80/month: amber soft warning ("Approaching this month's
 *   $100 AI usage limit").
 * - At ≥ $100/month: red hard-block message ("Limit reached — chat
 *   is paused for this month. Contact Morgan to lift the cap.").
 *
 * Wired below `pendingProposals` and above `ChatInput` in
 * `UniversalChatPanel`. Plugs into `getOrgUsageStatus` (reactive).
 */

import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { AlertTriangle, OctagonAlert } from "lucide-react";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { cn } from "@/utils/misc";

interface UsageBannerProps {
  orgId: Id<"organizations">;
  className?: string;
}

export function UsageBanner({ orgId, className }: UsageBannerProps) {
  const { data } = useQuery(
    convexQuery(api.lib.aiUsageQueries.getOrgUsageStatus, { orgId }),
  );

  if (!data) return null;
  if (!data.softWarn && !data.hardBlock) return null;

  // Keep the copy short. The detailed used/cap numbers, period, and
  // per-surface breakdown live in Settings → Billing on the AiUsageCard
  // — the banner is just a quick "you're capped, here's where to look".
  if (data.hardBlock) {
    return (
      <div
        className={cn(
          "mx-3 my-2 flex items-start gap-2.5 rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2.5 shadow-sm",
          className,
        )}
      >
        <OctagonAlert className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-destructive">
            You've hit your AI usage limit
          </p>
          <p className="text-xs text-foreground/80">
            Chat is paused until next month. Contact Morgan support to lift the
            cap.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "mx-3 my-2 flex items-start gap-2.5 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2.5 shadow-sm",
        className,
      )}
    >
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-foreground">
          You're approaching your AI usage limit
        </p>
        <p className="text-xs text-foreground/80">
          Contact Morgan support if you need a higher monthly cap.
        </p>
      </div>
    </div>
  );
}
